import * as THREE from "three";
import { WORLD_SIZE } from "./constants.js";
import vertexPars from "../shaders/terrain/vertexPars.glsl";
import beginNormal from "../shaders/terrain/beginNormal.glsl";
import beginVertex from "../shaders/terrain/beginVertex.glsl";
import worldPos from "../shaders/terrain/worldPos.glsl";
import fragmentPars from "../shaders/terrain/fragmentPars.glsl";
import colorChunk from "../shaders/terrain/color.glsl";
import aoChunk from "../shaders/terrain/ao.glsl";
import fogChunk from "../shaders/terrain/fog.glsl";

function srgb(r, g, b) {
    return new THREE.Color().setRGB(r, g, b, THREE.SRGBColorSpace);
}

export default class TerrainMaterial {
    constructor(debug) {
        this.baseData = new Float32Array(WORLD_SIZE * WORLD_SIZE);
        this.baseData.fill(18);
        this.baseTexture = new THREE.DataTexture(
            this.baseData,
            WORLD_SIZE,
            WORLD_SIZE,
            THREE.RedFormat,
            THREE.FloatType,
        );
        this.baseTexture.magFilter = THREE.NearestFilter;
        this.baseTexture.minFilter = THREE.NearestFilter;
        this.baseTexture.wrapS = THREE.ClampToEdgeWrapping;
        this.baseTexture.wrapT = THREE.ClampToEdgeWrapping;
        this.baseTexture.generateMipmaps = false;
        this.baseTexture.colorSpace = THREE.NoColorSpace;
        this.baseTexture.flipY = false;
        this.baseTexture.needsUpdate = true;

        this.strataLow = srgb(0.29, 0.1, 0.05);
        this.strataMid = srgb(0.58, 0.26, 0.12);
        this.strataHigh = srgb(0.74, 0.4, 0.22);
        this.topDust = srgb(0.89, 0.58, 0.34);
        this.fogColor = new THREE.Color(0.72, 0.48, 0.32);
        this.sunDir = new THREE.Vector3(0.4, 0.6, 0.3).normalize();

        this.uniforms = {
            uBaseHeights: { value: this.baseTexture },
            uWorldSize: { value: WORLD_SIZE },
            uWorldHalf: { value: WORLD_SIZE / 2 },
            uVariation: { value: 0.22 },
            uStrataLow: { value: this.strataLow },
            uStrataMid: { value: this.strataMid },
            uStrataHigh: { value: this.strataHigh },
            uTopDust: { value: this.topDust },
            uFogColor: { value: this.fogColor },
            uFogDensity: { value: 0.0028 },
            uFogHeightFalloff: { value: 0.018 },
            uFogStart: { value: 70 },
            uSunScatter: { value: 0.45 },
            uSunDir: { value: this.sunDir },
            uFlatStart: { value: 100 },
            uFlatEnd: { value: 300 },
            uStormDensity: { value: 0 },
            uStormOrigin: { value: new THREE.Vector3() },
        };

        this.material = new THREE.MeshLambertMaterial({
            color: 0xffffff,
            fog: false,
            dithering: true,
        });
        this.material.customProgramCacheKey = () => "mars-terrain-v2";
        this.material.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, this.uniforms);
            shader.vertexShader = vertexPars + "\n" + shader.vertexShader;
            shader.vertexShader = shader.vertexShader
                .replace("#include <beginnormal_vertex>", beginNormal)
                .replace("#include <begin_vertex>", beginVertex)
                .replace("#include <worldpos_vertex>", worldPos);
            shader.fragmentShader = fragmentPars + "\n" + shader.fragmentShader;
            shader.fragmentShader = shader.fragmentShader
                .replace("#include <color_fragment>", colorChunk)
                .replace("#include <opaque_fragment>", aoChunk)
                .replace("#include <fog_fragment>", fogChunk);
        };

        this.setDebug(debug);
    }

    setBaseHeights(baseHeights) {
        const data = this.baseData;
        for (let i = 0; i < data.length; i++) data[i] = baseHeights[i];
        this.baseTexture.needsUpdate = true;
    }

    setDebug(debug) {
        if (!debug?.active) return;

        const folder = debug.ui.addFolder("Terrain look");
        debug.addColor(folder, this.strataLow, "low");
        debug.addColor(folder, this.strataMid, "mid");
        debug.addColor(folder, this.strataHigh, "high");
        debug.addColor(folder, this.topDust, "dust");
        folder
            .add(this.uniforms.uVariation, "value", 0, 0.6, 0.01)
            .name("variation");
        folder
            .add(this.uniforms.uFlatStart, "value", 0, 1000, 1)
            .name("far flatten start");
        folder
            .add(this.uniforms.uFlatEnd, "value", 1, 1200, 1)
            .name("far flatten end");
        folder.close();
    }
}
